import './SlideEditor.scss';
import cn from 'classnames';
import * as R from 'ramda';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import {
  MdCheck,
  MdCheckCircle,
  MdClose,
  MdDelete,
  MdDeleteOutline,
  MdEdit,
  MdErrorOutline,
  MdInfoOutline,
  MdLogout,
  MdOutlineContentCopy,
  MdOutlineError,
  MdOutlineVisibility,
  MdOutlineVisibilityOff,
  MdPriorityHigh,
  MdSave
} from 'react-icons/md';
import {
  ParameterConfigurationType,
  SlideInputGroup,
  TemplateConfig
} from '@blisspointmedia/bpm-types/dist/SlidesV2';
import { getSlideKey, isMetricConfigFilled } from '@blisspointmedia/bpm-types/dist/SlidesV2Rules';
import { checkGoogleAccessTokens } from '@src/utils/auth';
import { AlertDialog } from '../../Components/AlertDialog';
import { Button } from '../../Components/Button';
import { DateConfig, DatePicker } from '../../Components/DatePicker/DatePicker';
import { Appearance, Design, Intent, Placement, Size } from '../../Components/designConstants';
import { InteractiveDiv } from '../../Components/InteractiveEl';
import { Spinner } from '../../Components/Spinner';
import { useSetError } from '../../redux/modals';
import { SlidesLambdaFetch } from '../../utils/fetch-utils';
import { Mixpanel, MxE } from '../../utils/mixpanelWrapper';
import { EditConfigMetadataModal } from './ConfigMetadataModal';
import { GenerationStatusDialog } from './GenerationStatusDialog';
import { InputsPanel } from './InputsPanel';
import { readJobIdFromResponse } from './presentation-status-utils';
import {
  buildDeckState,
  canDeleteSlide,
  Edits,
  generateLocalId,
  getAllCurrentValues,
  getDuplicateKey,
  hasRejectedValue,
  processSlideParameters,
  resolveParameterValue,
  SlideInstance,
  SlideSource
} from './utils';

interface SlideEditorProps {
  template: TemplateConfig;
  slideIdToSignedUrl: Record<string, string>;
  onClose: () => void;
  company: string;
  onSaveConfigSuccess: (newConfigId: string) => Promise<void>;
}

/**
 * Initialize slide instances from template. It makes sure that they are sorted correctly and
 * adds additional metadata to the slides.
 */
const initializeSlideInstances = (template: TemplateConfig): SlideInstance[] => {
  const slides: SlideInstance[] = [];
  let currentSlideNumber = 1;

  // Group slides by slideId to handle originals + loops
  const slidesBySlideId = template.slides.reduce(
    (acc, slide) => {
      if (!acc[slide.slideId]) {
        acc[slide.slideId] = [];
      }
      acc[slide.slideId].push(slide);
      return acc;
    },
    {} as Record<string, typeof template.slides>
  );

  // Process each group of slides (original + its loops)
  Object.values(slidesBySlideId).forEach(slideGroup => {
    // Sort the group: original first, then loops by loopNumber
    const sortedGroup = slideGroup.sort((a, b) => {
      if (!a.isLoop && b.isLoop) {
        return -1;
      }
      if (a.isLoop && !b.isLoop) {
        return 1;
      }
      if (a.isLoop && b.isLoop) {
        return (a.loopNumber || 0) - (b.loopNumber || 0);
      }
      return 0;
    });

    // Add original and loops
    sortedGroup.forEach(slide => {
      const managedSlide: SlideInstance = {
        ...slide,
        key: getSlideKey(slide),
        slideNumber: currentSlideNumber++,
        source: slide.isLoop ? SlideSource.PRESET_LOOP : SlideSource.ORIGINAL,
        originalSlideId: slide.slideId,
        isSoftDeleted: false
      };
      slides.push(managedSlide);
    });
  });

  return slides;
};

export const SlideEditor = ({
  template,
  slideIdToSignedUrl,
  onClose,
  company,
  onSaveConfigSuccess
}: SlideEditorProps) => {
  const setError = useSetError();
  const [activeSlideId, setActiveSlideId] = useState<string | null>(null);
  const [showUnsavedChangesDialog, setShowUnsavedChangesDialog] = useState(false);
  const [showDiscardChangesDialog, setShowDiscardChangesDialog] = useState(false);
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [generationJobId, setGenerationJobId] = useState<string>();
  const [edits, setEdits] = useState<Edits>({});
  const [generating, setGenerating] = useState(false);
  const [savingConfig, setSavingConfig] = useState(false);
  const [newConfigName, setNewConfigName] = useState<string | undefined>();
  const [newConfigDescription, setNewConfigDescription] = useState<string | undefined>();
  const [showEditMetadataModal, setShowEditMetadataModal] = useState(false);
  const [shouldSave, setShouldSave] = useState(false);
  const [slideInstances, setSlideInstances] = useState<SlideInstance[]>([]);
  const [skippedSlides, setSkippedSlides] = useState<Record<string, boolean>>({});
  // Store the initial disabled slides state separately to detect changes and to be able to reset to initial state
  const [initialSkippedSlides, setInitialSkippedSlides] = useState<Record<string, boolean>>({});
  const [globalDateConfig, setGlobalDateConfig] = useState<DateConfig | undefined>();

  // Initialize slideInstances from template
  useEffect(() => {
    setSlideInstances(initializeSlideInstances(template));
  }, [template]);

  // Initialize skippedSlides and initialSkippedSlides with the template's isDisabled values, and
  // reinitialize them whenever the template changes (e.g., after refetch after saving a config)
  useEffect(() => {
    const newSkippedSlides: Record<string, boolean> = {};
    const newInitialSkippedSlides: Record<string, boolean> = {};

    template.slides.forEach(slide => {
      const slideKey = getSlideKey(slide);
      const isDisabled = slide.isDisabled || false;
      newSkippedSlides[slideKey] = isDisabled;
      newInitialSkippedSlides[slideKey] = isDisabled;
    });

    setSkippedSlides(newSkippedSlides);
    setInitialSkippedSlides(newInitialSkippedSlides);
  }, [template]);

  // Set a default name for a new deck based on a blueprint
  useEffect(() => {
    if (!template.configId) {
      setNewConfigName(`${template.templateName} (Copy)`);
    }
  }, [template]);

  // Process slides - filter out soft-deleted slides and renumber them
  const processedSlides = useMemo(() => {
    let currentSlideNumber = 1;

    return slideInstances
      .filter(slide => !slide.isSoftDeleted)
      .map(slide => ({
        ...slide,
        slideNumber: currentSlideNumber++
      }));
  }, [slideInstances]);

  /**
   * Returns the slides and parameters that have date parameters.
   */
  const dateParameterTargets = useMemo(() => {
    const primaryOnly: { slideKey: string; paramKey: string }[] = [];
    const primaryAndComparison: { slideKey: string; paramKey: string }[] = [];

    for (const slide of processedSlides) {
      for (const param of slide.parameters) {
        if (param.type === ParameterConfigurationType.PRIMARY_ONLY_DATE_PICKER) {
          primaryOnly.push({ slideKey: slide.key, paramKey: param.key });
        } else if (param.type === ParameterConfigurationType.PRIMARY_AND_COMPARISON_DATE_PICKER) {
          primaryAndComparison.push({ slideKey: slide.key, paramKey: param.key });
        }
      }
    }

    return { primaryOnly, primaryAndComparison };
  }, [processedSlides]);

  const hasUnsavedChanges = useMemo(() => {
    // Check if skippedSlides differ from initialSkippedSlides
    const skippedSlidesChanged = !R.equals(skippedSlides, initialSkippedSlides);
    // Check if there are any local duplicates or soft-deleted slides
    const hasLocalChanges = slideInstances.some(
      slide => slide.source === SlideSource.LOCAL_DUPLICATE || slide.isSoftDeleted
    );

    return Boolean(
      Object.keys(edits).length > 0 ||
      skippedSlidesChanged ||
      hasLocalChanges ||
      newConfigName ||
      newConfigDescription
    );
  }, [
    edits,
    skippedSlides,
    initialSkippedSlides,
    slideInstances,
    newConfigName,
    newConfigDescription
  ]);

  const initialInputValues = useMemo(() => {
    const init: Record<string, Record<string, unknown>> = {};

    for (const slide of processedSlides) {
      if (!init[slide.key]) {
        init[slide.key] = {};
      }

      // Map of inputId to parameter
      const parameterMap = slide.parameters.reduce(
        (acc, param) => {
          acc[param.inputId] = param;
          return acc;
        },
        {} as Record<string, (typeof slide.parameters)[number]>
      );

      for (const group of slide.inputGroups) {
        for (const inputId of group.inputIds) {
          const parameter = parameterMap[inputId];
          if (parameter) {
            // Use the parameter's default value
            init[slide.key][parameter.key] = parameter.defaultValue ?? null;
          }
        }
      }
    }

    return init;
  }, [processedSlides]);

  const currentSlideInitValues = useMemo(
    () => initialInputValues[activeSlideId || ''] || {},
    [initialInputValues, activeSlideId]
  );

  const currentSlideEdits = useMemo(() => edits[activeSlideId || ''] || {}, [edits, activeSlideId]);

  useEffect(() => {
    if (!activeSlideId && processedSlides.length > 0) {
      setActiveSlideId(processedSlides[0].key);
    }
  }, [processedSlides, activeSlideId]);

  const activeSlide = useMemo(
    () => processedSlides.find(s => s.key === activeSlideId) || processedSlides[0],
    [processedSlides, activeSlideId]
  );

  const getInputGroupsForSlide = useCallback(
    (slideKey: string): SlideInputGroup[] => {
      const slide = processedSlides.find(s => s.key === slideKey);
      return slide?.inputGroups || [];
    },
    [processedSlides]
  );

  const getTotalInputsForSlide = useCallback(
    (slideKey: string): number => {
      const groups = getInputGroupsForSlide(slideKey);
      return groups.reduce((acc, g) => acc + (g.inputIds?.length || 0), 0);
    },
    [getInputGroupsForSlide]
  );

  const getFilledInputCountForSlide = useCallback(
    (slideKey: string): number => {
      const slide = processedSlides.find(s => s.key === slideKey);
      if (!slide) {
        return 0;
      }

      let filledCount = 0;

      // Map of inputId to parameters for this slide
      const parameterMap = slide.parameters.reduce(
        (acc, param) => {
          acc[param.inputId] = param;
          return acc;
        },
        {} as Record<string, (typeof slide.parameters)[number]>
      );

      const slideEdits = edits[slideKey];
      const allCurrentValues = getAllCurrentValues(slideEdits, initialInputValues[slideKey]);
      for (const group of slide.inputGroups) {
        for (const inputId of group.inputIds) {
          const parameter = parameterMap[inputId];
          if (parameter) {
            const value = resolveParameterValue(
              parameter.key,
              slideEdits,
              initialInputValues[slideKey]
            );
            const isMetricConfigType =
              parameter.type === ParameterConfigurationType.METRIC_CONFIG_DROPDOWN ||
              parameter.type === ParameterConfigurationType.METRIC_CONFIG_WITHOUT_SOURCE_DROPDOWN;
            const isFilled =
              value !== undefined &&
              value !== null &&
              (typeof value !== 'string' || value.length > 0) &&
              (typeof value !== 'object' ||
                (isMetricConfigType ? isMetricConfigFilled(value, parameter) : true));
            if (isFilled && !hasRejectedValue(parameter, allCurrentValues)) {
              filledCount++;
            }
          }
        }
      }

      return filledCount;
    },
    [edits, processedSlides, initialInputValues]
  );

  const inputGroups: SlideInputGroup[] = useMemo(() => {
    if (!activeSlide) {
      return [];
    }
    return getInputGroupsForSlide(activeSlide.key);
  }, [activeSlide, getInputGroupsForSlide]);

  const handleChange = (slideKey: string, parameterName: string, value: unknown) => {
    setEdits(prev => {
      const prevInputs = prev[slideKey] || {};
      return {
        ...prev,
        [slideKey]: {
          ...prevInputs,
          [parameterName]: value
        }
      };
    });
  };

  /**
   * Handles the global date change for all slides with date parameters.
   */
  const handleGlobalDateChange = useCallback(
    (dateInfo: DateConfig) => {
      const primaryFields = {
        start: dateInfo.start,
        end: dateInfo.end,
        selectedPreset: dateInfo.selectedPreset,
        startCustomPreset: dateInfo.startCustomPreset,
        endCustomPreset: dateInfo.endCustomPreset
      };

      const comparisonFields = {
        comparisonStart: dateInfo.comparisonStart,
        comparisonEnd: dateInfo.comparisonEnd,
        selectedCompPerType: dateInfo.selectedCompPerType,
        startCompCustomPreset: dateInfo.startCompCustomPreset,
        endCompCustomPreset: dateInfo.endCompCustomPreset
      };

      setEdits(prev => {
        const next = { ...prev };

        for (const { slideKey, paramKey } of dateParameterTargets.primaryOnly) {
          next[slideKey] = {
            ...next[slideKey],
            [paramKey]: {
              ...(next[slideKey]?.[paramKey] as object | undefined),
              ...primaryFields
            }
          };
        }

        for (const { slideKey, paramKey } of dateParameterTargets.primaryAndComparison) {
          next[slideKey] = {
            ...next[slideKey],
            [paramKey]: {
              ...(next[slideKey]?.[paramKey] as object | undefined),
              ...primaryFields,
              ...comparisonFields
            }
          };
        }

        return next;
      });

      setGlobalDateConfig({
        ...primaryFields,
        ...comparisonFields
      });
    },
    [dateParameterTargets]
  );

  const handleConstraintClearedParameterValue = useCallback(
    (payload: {
      parameterLabel: string;
      description?: string;
      change?: { kind: 'cleared'; from: string } | { kind: 'multiRemoved'; removed: string[] };
    }) => {
      const { parameterLabel, change, description } = payload;
      let base: string;
      if (change?.kind === 'cleared') {
        base = `"${parameterLabel}" was cleared (was "${change.from}") due to a constraint. Please reselect a valid value.`;
      } else if (change?.kind === 'multiRemoved') {
        const removed = change.removed.map(r => `"${r}"`).join(', ');
        base = `"${parameterLabel}" had ${removed} removed due to a constraint.`;
      } else {
        // Fallback for hidden non-string inputs (dates, metric configs) where we can't resolve a label.
        base = `"${parameterLabel}" was updated because of a constraint.`;
      }
      const message = description ? `${base} ${description}` : base;
      setError({ title: 'Input Updated', intent: Intent.WARNING, message, hideCancel: true });
    },
    [setError]
  );

  const clearLocalChanges = useCallback(() => {
    setEdits({});
    setSlideInstances(initializeSlideInstances(template));
    setSkippedSlides(initialSkippedSlides);
    setNewConfigName(undefined);
    setNewConfigDescription(undefined);
    setGlobalDateConfig(undefined);
  }, [initialSkippedSlides, template]);

  const handleCloseRequest = () => {
    if (!hasUnsavedChanges) {
      clearLocalChanges();
      onClose();
    } else {
      setShowUnsavedChangesDialog(true);
    }
  };

  const handleDiscardRequest = () => {
    if (!hasUnsavedChanges) {
      clearLocalChanges();
    } else {
      setShowDiscardChangesDialog(true);
    }
  };

  /**
   * Duplicate a slide and add it to the slide instances array in the correct position.
   */
  const duplicateSlide = useCallback(
    (slideKey: string) => {
      try {
        const slideToDuplicate = slideInstances.find(s => s.key === slideKey);
        if (!slideToDuplicate) {
          return;
        }

        // Give the duplicate slide a temporary unique ID
        const localId = generateLocalId();

        // Create a new slide instance for the duplicate
        const newDuplicate: SlideInstance = {
          ...slideToDuplicate,
          key: getDuplicateKey(slideToDuplicate.slideId, localId),
          source: SlideSource.LOCAL_DUPLICATE,
          originalSlideId: slideToDuplicate.slideId,
          localId,
          duplicateOfKey: slideToDuplicate.duplicateOfKey ?? slideToDuplicate.key,
          isSoftDeleted: false,
          isLoop: false, // Local duplicates are not considered loops
          loopNumber: undefined
        };

        // Insert the new duplicate at the correct position
        setSlideInstances(prev => {
          // Find the insertion point - after all slides with the same slideId
          const { slideId } = slideToDuplicate;
          let insertIndex = prev.length; // Default to end

          // Find the last slide that belongs to this slideId group
          for (let i = prev.length - 1; i >= 0; i--) {
            const slide = prev[i];
            if (slide.slideId === slideId || slide.originalSlideId === slideId) {
              insertIndex = i + 1;
              break;
            }
          }

          const newSlides = [...prev];
          newSlides.splice(insertIndex, 0, newDuplicate);
          return newSlides;
        });

        // Copy any pending edits from the original slide to the duplicate
        const editsForSlideToDuplicate = edits[slideKey];
        if (editsForSlideToDuplicate) {
          setEdits(prev => {
            const prevEdits = { ...prev };
            prevEdits[newDuplicate.key] = editsForSlideToDuplicate;
            return prevEdits;
          });
        }
      } catch (e) {
        setError({
          message: `Error duplicating slide. ${e.message}`,
          reportError: e
        });
      }
    },
    [slideInstances, edits, setError]
  );

  /**
   * Delete a slide - hard delete local duplicates, soft delete preset slides
   */
  const deleteSlide = useCallback(
    (slideKey: string) => {
      const slideToDelete = slideInstances.find(s => s.key === slideKey);
      if (!slideToDelete) {
        return;
      }

      if (slideToDelete.source === SlideSource.LOCAL_DUPLICATE) {
        // Hard delete local duplicates
        setSlideInstances(prev => prev.filter(s => s.key !== slideKey));
      } else if (slideToDelete.source === SlideSource.PRESET_LOOP) {
        // Soft delete preset loop slides
        setSlideInstances(prev =>
          prev.map(s => (s.key === slideKey ? { ...s, isSoftDeleted: true } : s))
        );
      }
      // Reset the active slide so that the inputs panel isn't pointing to a deleted slide
      setActiveSlideId(processedSlides[0].key);
    },
    [slideInstances, processedSlides]
  );

  const toggleSlideVisibility = () => {
    if (!activeSlideId) {
      return;
    }
    setSkippedSlides(prev => ({ ...prev, [activeSlideId]: !prev[activeSlideId] }));
  };

  const buildSlidePayload = useCallback(
    /*
     includeSoftDeleted is true when processing edits for generation, and false for when saving config.
     This is because when someone generates a presentation after locally deleting a slide, we want to include
     it in the payload as isDisabled = true, so we know to delete it from the presentation. When saving a config, we don't
     want to include soft-deleted slides because we want to permanently delete them from the config.
    */
    (includeSoftDeletedSlides = false) => {
      // Counter of duplicate slides per slideId to assign sequential loop numbers
      const loopCounters: Record<string, number> = {};

      const slides = slideInstances
        // Filter out soft-deleted slides for saving config
        .filter(slide => includeSoftDeletedSlides || !slide.isSoftDeleted)
        .map(slide => {
          const { key, slideId, source, dynamicContentName, isSoftDeleted } = slide;
          // Process parameters for this slide
          const parameters = processSlideParameters(slide, edits, initialInputValues);

          // Calculate loop number based on slide type
          let computedLoopNumber;

          if (source === SlideSource.PRESET_LOOP || source === SlideSource.LOCAL_DUPLICATE) {
            loopCounters[slideId] = (loopCounters[slideId] || 0) + 1;
            computedLoopNumber = loopCounters[slideId];
          }

          const isLoop =
            source === SlideSource.PRESET_LOOP || source === SlideSource.LOCAL_DUPLICATE;

          return {
            key: getSlideKey({
              slideId,
              isLoop,
              loopNumber: computedLoopNumber
            }),
            slideId,
            dynamicContentName,
            parameters,
            isDisabled: includeSoftDeletedSlides
              ? skippedSlides[key] || isSoftDeleted || false
              : skippedSlides[key] || false,
            isLoop,
            loopNumber: computedLoopNumber
          };
        });
      return slides;
    },
    [slideInstances, edits, skippedSlides, initialInputValues]
  );

  const buildSlidePayloadForGeneration = useCallback(
    () => buildSlidePayload(true),
    [buildSlidePayload]
  );
  const buildSlidePayloadForSavingConfig = useCallback(
    () => buildSlidePayload(false),
    [buildSlidePayload]
  );

  const generatePresentation = useCallback(
    // If singleSlideKey is provided, generate only that slide
    async (singleSlideKey?: string) => {
      try {
        setGenerating(true);
        const baseSlides = buildSlidePayloadForGeneration();
        // If we're exporting a single slide, mark all other slides in presentation as skipped
        const slides = singleSlideKey
          ? baseSlides.map(slide => ({ ...slide, isDisabled: slide.key !== singleSlideKey }))
          : baseSlides;
        const deckState = buildDeckState(slideInstances, slides, edits, initialInputValues);

        // Generating is the point where we need Google Drive access, so acquire it here on this
        // click (the click is the user gesture that lets Google's consent prompt open — there is no
        // separate "Connect" step). checkGoogleAccessTokens surfaces its own error on failure (e.g.
        // prompt declined, or signed in with email/password), so just stop if it doesn't succeed.
        const googleAccessToken = await checkGoogleAccessTokens(setError);
        if (!googleAccessToken) {
          setGenerating(false);
          return;
        }

        const payload = {
          blueprintId: Number(template.blueprintId),
          configId: template.configId ? Number(template.configId) : undefined,
          company,
          accessToken: googleAccessToken,
          slides,
          deckState
        };

        const response = await SlidesLambdaFetch('/generatePresentation', {
          method: 'POST',
          body: JSON.stringify({ ...payload, testCompanyPageKey: 'SLIDES' })
        });

        setGenerationJobId(await readJobIdFromResponse(response));
        setShowSaveDialog(true);
        setGenerating(false);
        Mixpanel.track(MxE.SlideGeneratorGeneratePresentation);
      } catch (e) {
        setError({
          message: `Error generating presentation. ${e.message}`,
          reportError: e
        });
        setGenerating(false);
      }
    },
    [
      company,
      buildSlidePayloadForGeneration,
      slideInstances,
      edits,
      initialInputValues,
      setError,
      template.configId,
      template.blueprintId
    ]
  );

  const handleSave = useCallback(
    /*
    isExistingConfig is true when saving to an existing config, and false when saving as a new config.
    */
    async (
      isExistingConfig: boolean,
      configNameOverride?: string,
      configDescriptionOverride?: string
    ) => {
      try {
        setSavingConfig(true);
        const payload = {
          company,
          blueprintId: template.blueprintId,
          // If saving to an existing config, include the configId in the payload. Otherwise, it
          // will be saved as a new config and we'll get a new configId in the response.
          ...(isExistingConfig && { configId: template.configId }),
          configName: configNameOverride || newConfigName || template.templateName,
          description: configDescriptionOverride || newConfigDescription || template.description,
          slides: buildSlidePayloadForSavingConfig()
        };

        const response = await SlidesLambdaFetch('/saveSlidesConfig', {
          method: 'POST',
          body: JSON.stringify(payload)
        });
        const responseData = await response.json();

        // If saving to an existing config, the response will be the same configId as the one we passed in.
        // If saving as a new config, the response will be a new configId.
        const { configId } = responseData;

        clearLocalChanges();

        // Refetch the template config and list of templates
        if (onSaveConfigSuccess) {
          await onSaveConfigSuccess(configId);
        }

        // Reset deck name after refetch for new configs since the config now has the updated name
        if (!isExistingConfig) {
          setNewConfigName(undefined);
        }

        if (isExistingConfig) {
          Mixpanel.track(MxE.SlideGeneratorSaveEdits);
        } else {
          Mixpanel.track(MxE.SlideGeneratorSaveAsNewTemplate);
        }

        setSavingConfig(false);
      } catch (e) {
        setSavingConfig(false);
        setError({
          message: `Error saving ${isExistingConfig ? 'an existing' : 'a new'} template. ${e.message}`,
          reportError: e
        });
      }
    },
    [
      company,
      setError,
      buildSlidePayloadForSavingConfig,
      template.description,
      template.blueprintId,
      template.templateName,
      template.configId,
      clearLocalChanges,
      onSaveConfigSuccess,
      newConfigName,
      newConfigDescription
    ]
  );

  const activeSlideFilledInputCount = getFilledInputCountForSlide(activeSlide?.key || '');
  const activeSlideTotal = getTotalInputsForSlide(activeSlide?.key || '');
  const isActiveSlideComplete = activeSlideFilledInputCount === activeSlideTotal;

  // The number of slides that have inputs / need to be filled (excluding skipped slides)
  const slidesToConfigureCount = useMemo(
    () =>
      processedSlides.filter(slide => slide.inputGroups.length > 0 && !skippedSlides[slide.key])
        .length,
    [processedSlides, skippedSlides]
  );

  // The number of slides that have all their inputs filled (excluding skipped slides)
  const slidesCompletedCount = useMemo(
    () =>
      processedSlides.filter(
        slide =>
          slide.inputGroups.length > 0 &&
          !skippedSlides[slide.key] &&
          getFilledInputCountForSlide(slide.key) === getTotalInputsForSlide(slide.key)
      ).length,
    [processedSlides, getFilledInputCountForSlide, getTotalInputsForSlide, skippedSlides]
  );

  // You can only generate a presentation if all inputs for all slides are filled
  const canGeneratePresentation = useMemo(
    () => slidesCompletedCount === slidesToConfigureCount,
    [slidesCompletedCount, slidesToConfigureCount]
  );

  const handleSetConfigMetadata = useCallback(
    (nextName: string, nextDescription: string) => {
      setNewConfigName(nextName);
      setNewConfigDescription(nextDescription);
      setShowEditMetadataModal(false);

      if (shouldSave) {
        handleSave(false, nextName, nextDescription);
        setShouldSave(false);
      }
    },
    [handleSave, shouldSave]
  );

  if (!activeSlide) {
    return null;
  }

  const { slideNumber, key, source } = activeSlide;
  const canDelete = canDeleteSlide(source);
  const isSkipped = skippedSlides[activeSlideId || ''];
  const isDuplicateSlide =
    source === SlideSource.LOCAL_DUPLICATE || source === SlideSource.PRESET_LOOP;

  return (
    <div className='slides-editor' data-testid='slides-editor'>
      <div className='slides-editor-header'>
        <div className='slides-editor-header-left'>
          <div className='config-title' data-testid='slides-editor-title'>
            {newConfigName || template.templateName}
          </div>
          <Button
            data-testid='edit-metadata-button'
            className='icon-button'
            onClick={() => setShowEditMetadataModal(true)}
            appearance={Appearance.EMPTY}
            leadingElement={<MdEdit />}
            size={Size.SM}
          />
        </div>
        <div className='slides-editor-header-right'>
          <Button
            size={Size.LG}
            data-testid='slides-editor-close-button'
            className='icon-button'
            onClick={handleCloseRequest}
            appearance={Appearance.EMPTY}
            design={Design.NEUTRAL}
            leadingElement={<MdClose />}
            disabled={savingConfig}
          />
        </div>
      </div>
      <div className='slides-editor-toolbar'>
        {(dateParameterTargets.primaryOnly.length > 0 ||
          dateParameterTargets.primaryAndComparison.length > 0) && (
          <div className='toolbar-item'>
            <div className='label'>
              Global Date Range Editor
              <OverlayTrigger
                placement={Placement.TOP_START}
                overlay={
                  <Tooltip>
                    Edits the date ranges for all slides at once. <br />
                    You can override any slide individually, and <br />
                    individual slide edits won&apos;t affect this picker.
                  </Tooltip>
                }
              >
                <MdInfoOutline />
              </OverlayTrigger>
            </div>
            {dateParameterTargets.primaryAndComparison.length > 0 ? (
              <DatePicker
                comparison
                range={
                  globalDateConfig?.start && globalDateConfig?.end
                    ? { start: globalDateConfig.start, end: globalDateConfig.end }
                    : undefined
                }
                comparisonRange={
                  globalDateConfig?.comparisonStart && globalDateConfig?.comparisonEnd
                    ? {
                        start: globalDateConfig.comparisonStart,
                        end: globalDateConfig.comparisonEnd
                      }
                    : undefined
                }
                onChange={() => {}}
                comparisonOnChange={() => {}}
                primaryAndComparisonOnChange={handleGlobalDateChange}
                removeHeaderText
                datePresets
                dateConfig={globalDateConfig}
              />
            ) : (
              <DatePicker
                range={
                  globalDateConfig?.start && globalDateConfig?.end
                    ? { start: globalDateConfig.start, end: globalDateConfig.end }
                    : undefined
                }
                onChange={(dateInputs, { selectedPreset, startCustomPreset, endCustomPreset }) => {
                  handleGlobalDateChange({
                    start: dateInputs.start,
                    end: dateInputs.end,
                    selectedPreset,
                    startCustomPreset,
                    endCustomPreset
                  });
                }}
                removeHeaderText
                datePresets
                dateConfig={globalDateConfig}
              />
            )}
          </div>
        )}
      </div>
      <div className='slides-editor-body'>
        <div className='slides-thumbnails'>
          <div
            className={cn('progress-label', {
              done: slidesCompletedCount === slidesToConfigureCount,
              pending: slidesCompletedCount !== slidesToConfigureCount
            })}
          >
            {slidesCompletedCount}/{slidesToConfigureCount} Slides
            {slidesCompletedCount === slidesToConfigureCount ? (
              <MdCheckCircle />
            ) : (
              <MdErrorOutline />
            )}
          </div>
          <div className='thumbnails-list'>
            {processedSlides.map(slide => (
              <ThumbnailItem
                key={slide.key}
                slide={slide}
                isActive={slide.key === activeSlideId}
                isSkipped={Boolean(skippedSlides[slide.key])}
                imgSrc={slideIdToSignedUrl[slide.slideId]}
                filled={getFilledInputCountForSlide(slide.key)}
                total={(slide.inputGroups || []).reduce(
                  (acc, g) => acc + (g.inputIds?.length || 0),
                  0
                )}
                onSelect={() => setActiveSlideId(slide.key)}
              />
            ))}
          </div>
        </div>

        <div className='slide-main'>
          <div className='slide-main-toolbar'>
            <div className='slide-main-toolbar-left'>
              <div className='slide-number-label'>Slide {slideNumber}</div>
              {isDuplicateSlide && (
                <OverlayTrigger overlay={<Tooltip>Copy of a Blueprint slide</Tooltip>}>
                  <div className='duplicate-slide-indicator'>
                    <MdOutlineContentCopy />
                    <div>Duplicated</div>
                  </div>
                </OverlayTrigger>
              )}
            </div>
            <div className='slide-main-toolbar-right'>
              <Button
                data-testid='delete-slide-button'
                className='icon-button'
                onClick={() => deleteSlide(key)}
                appearance={Appearance.EMPTY}
                design={Design.NEUTRAL}
                leadingElement={<MdDeleteOutline />}
                size={Size.SM}
                disabled={!canDelete}
                tooltip={{
                  text: canDelete ? 'Delete slide' : 'Blueprint slides cannot be deleted',
                  placement: Placement.TOP,
                  class: 'slide-generator-button-tooltip'
                }}
              />

              <Button
                data-testid='skip-slide-button'
                className='icon-button'
                onClick={toggleSlideVisibility}
                appearance={Appearance.EMPTY}
                design={Design.NEUTRAL}
                leadingElement={isSkipped ? <MdOutlineVisibilityOff /> : <MdOutlineVisibility />}
                size={Size.SM}
                tooltip={{
                  text: isSkipped ? 'Include slide in export' : 'Exclude slide from export',
                  placement: Placement.TOP,
                  class: 'slide-generator-button-tooltip'
                }}
              />

              <Button
                data-testid='duplicate-slide-button'
                className='icon-button'
                onClick={() => duplicateSlide(key)}
                appearance={Appearance.EMPTY}
                design={Design.NEUTRAL}
                leadingElement={<MdOutlineContentCopy />}
                size={Size.SM}
                tooltip={{
                  text: 'Duplicate slide',
                  placement: Placement.TOP,
                  class: 'slide-generator-button-tooltip'
                }}
              />

              <Button
                data-testid='export-slide-button'
                className='icon-button'
                onClick={() => generatePresentation(key)}
                appearance={Appearance.EMPTY}
                design={Design.NEUTRAL}
                trailingElement={generating ? <Spinner size={18} /> : <MdLogout />}
                size={Size.SM}
                disabled={generating || !isActiveSlideComplete}
                tooltip={{
                  text: isActiveSlideComplete
                    ? 'Export slide'
                    : 'Cannot export with missing inputs',
                  placement: Placement.TOP,
                  class: 'slide-generator-button-tooltip'
                }}
              >
                Export Slide
              </Button>
            </div>
          </div>
          <div className='slide-main-content'>
            <div className={cn('slide-canvas', { hidden: isSkipped })}>
              <div className='visibility-badge'>
                {isSkipped ? <MdOutlineVisibilityOff /> : null}
              </div>
              <img src={slideIdToSignedUrl[activeSlide.slideId]} alt='Active slide' />
            </div>
            <div className='slide-main-right-side'>
              {activeSlideTotal > 0 && (
                <InputsProgressIndicator
                  activeSlideFilledInputCount={activeSlideFilledInputCount}
                  activeSlideTotal={activeSlideTotal}
                  isActiveSlideComplete={isActiveSlideComplete}
                />
              )}
              <InputsPanel
                inputGroups={inputGroups}
                currentSlideInitValues={currentSlideInitValues}
                currentSlideEdits={currentSlideEdits}
                activeSlide={activeSlide}
                handleChange={handleChange}
                onConstraintClearedParameterValue={handleConstraintClearedParameterValue}
              />
            </div>
          </div>
        </div>
      </div>

      <div className='slides-editor-footer'>
        <div className='left-footer-actions'>
          {hasUnsavedChanges && (
            <Button
              size={Size.LG}
              className='discard-changes-button'
              appearance={Appearance.EMPTY}
              leadingElement={<MdDelete />}
              onClick={handleDiscardRequest}
              disabled={!hasUnsavedChanges || generating}
            >
              Discard Changes
            </Button>
          )}
        </div>
        <div className='right-footer-actions'>
          {template.configId ? (
            <>
              <Button
                size={Size.LG}
                leadingElement={<MdSave />}
                disabled={!hasUnsavedChanges || generating || savingConfig}
                loading={savingConfig}
                onClick={() => handleSave(true)}
              >
                Save Edits to Existing Template
              </Button>
              <Button
                size={Size.LG}
                appearance={Appearance.OUTLINED}
                leadingElement={<MdSave />}
                disabled={!hasUnsavedChanges || generating || savingConfig}
                loading={savingConfig}
                onClick={() => {
                  setShouldSave(true);
                  setShowEditMetadataModal(true);
                }}
              >
                Save as New Template
              </Button>
            </>
          ) : (
            <Button
              size={Size.LG}
              leadingElement={<MdSave />}
              disabled={!hasUnsavedChanges || generating || savingConfig}
              loading={savingConfig}
              onClick={() => {
                setShouldSave(true);
                setShowEditMetadataModal(true);
              }}
            >
              Save as New Template
            </Button>
          )}
          <Button
            leadingElement={generating ? <Spinner size={20} /> : <MdLogout />}
            disabled={generating || !canGeneratePresentation}
            onClick={() => generatePresentation()}
            tooltip={
              !canGeneratePresentation
                ? {
                    text: `${
                      slidesToConfigureCount - slidesCompletedCount
                    } slide(s) are incomplete. Complete or hide them to export the entire presentation.`,
                    placement: Placement.TOP_END,
                    class: 'slide-generator-button-tooltip'
                  }
                : undefined
            }
          >
            Export
          </Button>
        </div>
      </div>

      {showEditMetadataModal && (
        <EditConfigMetadataModal
          onHide={() => setShowEditMetadataModal(false)}
          onSave={handleSetConfigMetadata}
          currentName={newConfigName || template.templateName}
          currentDescription={newConfigDescription || template.description}
        />
      )}

      <AlertDialog
        intent={Intent.WARNING}
        show={showUnsavedChangesDialog}
        onHide={() => setShowUnsavedChangesDialog(false)}
        icon={null}
        title='Discard Changes?'
        body='You have unsaved changes, are you sure you want to exit? This action cannot be undone.'
        primaryAction={{
          onClick: () => {
            setShowUnsavedChangesDialog(false);
            clearLocalChanges();
            onClose();
          },
          label: 'Discard and Exit'
        }}
        secondaryAction={{ onClick: () => setShowUnsavedChangesDialog(false) }}
      />
      <AlertDialog
        intent={Intent.WARNING}
        show={showDiscardChangesDialog}
        onHide={() => setShowDiscardChangesDialog(false)}
        icon={null}
        title='Are you sure?'
        body='You have unsaved changes, are you sure you want to discard them? This action cannot be undone.'
        primaryAction={{
          onClick: () => {
            setShowDiscardChangesDialog(false);
            clearLocalChanges();
            setActiveSlideId(processedSlides[0].key);
          },
          label: 'Discard Changes'
        }}
        secondaryAction={{ onClick: () => setShowDiscardChangesDialog(false) }}
      />
      {showSaveDialog && (
        <GenerationStatusDialog jobId={generationJobId} onHide={() => setShowSaveDialog(false)} />
      )}
      {savingConfig && (
        <div className='slides-editor-loading-overlay'>
          <div className='loading-content'>
            <Spinner />
            <div className='loading-text'>Saving template...</div>
          </div>
        </div>
      )}
    </div>
  );
};

interface ThumbnailItemProps {
  slide: SlideInstance;
  isActive: boolean;
  isSkipped: boolean;
  imgSrc?: string;
  filled: number;
  total: number;
  onSelect: () => void;
}

const ThumbnailItem = ({
  slide,
  isActive,
  isSkipped,
  imgSrc,
  filled,
  total,
  onSelect
}: ThumbnailItemProps) => {
  // If there are no inputs for a slide, don't show a badge
  let thumbnailBadge;
  if (total === 0) {
    thumbnailBadge = null;
  } else if (filled === total) {
    thumbnailBadge = <MdCheck />;
  } else {
    thumbnailBadge = <MdPriorityHigh />;
  }

  const isDuplicateSlide =
    slide.source === SlideSource.LOCAL_DUPLICATE || slide.source === SlideSource.PRESET_LOOP;

  return (
    <InteractiveDiv
      data-testid='thumbnail-item'
      className={cn('thumbnail-item', {
        active: isActive,
        hidden: isSkipped,
        'loop-slide': slide.isLoop
      })}
      onClick={onSelect}
    >
      <div className='thumbnail-number'>
        <div>{slide.slideNumber}</div>
        <div className='duplicate-badge'>
          {isDuplicateSlide ? (
            <OverlayTrigger overlay={<Tooltip>Duplicated slide</Tooltip>}>
              <MdOutlineContentCopy />
            </OverlayTrigger>
          ) : (
            ''
          )}
        </div>
      </div>
      <div className='thumbnail-container'>
        <div className='visibility-badge'>{isSkipped ? <MdOutlineVisibilityOff /> : null}</div>
        <img
          src={imgSrc}
          alt={`Slide ${slide.slideNumber}${slide.isLoop ? ` (Loop ${slide.loopNumber})` : ''}`}
        />
        {thumbnailBadge && (
          <div
            className={cn('thumbnail-badge', {
              'slide-done': filled === total,
              'slide-pending': filled !== total
            })}
          >
            {thumbnailBadge}
          </div>
        )}
      </div>
    </InteractiveDiv>
  );
};

interface InputsProgressIndicatorProps {
  activeSlideFilledInputCount: number;
  activeSlideTotal: number;
  isActiveSlideComplete: boolean;
}

const InputsProgressIndicator = ({
  activeSlideFilledInputCount,
  activeSlideTotal,
  isActiveSlideComplete
}: InputsProgressIndicatorProps) => (
  <div
    className={cn('inputs-progress-indicator', {
      done: isActiveSlideComplete,
      pending: !isActiveSlideComplete
    })}
  >
    {activeSlideTotal > 0 && (
      <div className='progress-indicator-text'>
        {activeSlideFilledInputCount}/{activeSlideTotal} Inputs
      </div>
    )}
    {activeSlideTotal > 0 && (
      <div className='progress-indicator-icon'>
        {isActiveSlideComplete ? <MdCheckCircle /> : <MdOutlineError />}
      </div>
    )}
  </div>
);
